import React, { FC, useCallback, useEffect, useState } from 'react';
import Alert from 'components/padi-ui/alert';
import AvatarUpload from 'components/padi-ui/avatar-upload';
import { useFormat } from 'helpers/hooks/useFormat';
import useGetUserInfo from 'helpers/hooks/useGetUserInfo';
import { useAccount } from 'frontastic';

export interface AvatarSectionProps {
  noPhotoAlert?: string;
  photoInfoMessage?: string;
  onAvatarUpdate?: (hasAvatar: boolean) => void;
  hasAvatar?: boolean;
}

const AvatarSection: FC<AvatarSectionProps> = ({
  noPhotoAlert,
  photoInfoMessage,
  onAvatarUpdate,
  hasAvatar = false,
}) => {
  const { idToken, userData } = useGetUserInfo();
  const { getProjectConfig } = useAccount();
  const { formatMessage: formatMessagePadi } = useFormat({ name: 'padi' });

  const [isLoading, setIsLoading] = useState(false);
  const [showAvatarUpload, setShowAvatarUpload] = useState(false);
  const [avatar, setAvatar] = useState<string>('');
  const [affiliateId, setAffiliateId] = useState('');

  // Get users avatar.
  const getUsersAvatar = useCallback(
    async (idToken: string, affiliateId: string) => {
      if (!idToken || !affiliateId) return;
      const proApiDomain = await getProjectConfig('EXTENSION_PADI_APIS_PRO_API_DOMAIN');
      if (proApiDomain.isError) return;

      await fetch(`${proApiDomain.setting}/certifications/photos/getphoto`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ affiliateId: affiliateId }),
        method: 'post',
      })
        .then((response) => response.blob())
        .then((data) => {
          const objectUrl = URL.createObjectURL(data);
          if (data?.size !== 0) {
            setAvatar(objectUrl);
            onAvatarUpdate?.(true);
          }
        });
    },
    [getProjectConfig, onAvatarUpdate],
  );

  // Get users certificates to check if they have avatar.

  useEffect(() => {
    if (idToken && userData) {
      if (!affiliateId) setAffiliateId(userData['custom:affiliate_id']);
      getUsersAvatar(idToken, affiliateId ?? userData['custom:affiliate_id']);
    }
  }, [affiliateId, getUsersAvatar, idToken, userData]);

  const avatarUploadIsOpen = (state: boolean) => {
    setShowAvatarUpload(state);
    if (!state && idToken) getUsersAvatar(idToken, affiliateId);
  };

  const handleAvatarUpload = () => {
    setShowAvatarUpload(true);
  };

  return (
    <div className="my-10 border-t border-neutral-400">
      <div className="my-15 text-base font-bold">
        {formatMessagePadi({ id: 'card.photo', defaultMessage: 'Card Photo' })}
      </div>
      <div className="flex justify-between rounded-md border border-gray-300 bg-blue-100 p-20">
        <div className="flex flex-col justify-center">
          {hasAvatar && (
            <div className="mb-10 aspect-square size-80">
              {avatar && <img className="size-full rounded-full object-cover" src={avatar} alt="profile image" />}
            </div>
          )}
          <div className="w-100">
            <button className="text-xs font-bold text-padi-blue" type="button" onClick={handleAvatarUpload}>
              {formatMessagePadi({ id: 'upload.photo', defaultMessage: 'Upload photo' })}
            </button>
            <AvatarUpload modalIsOpen={showAvatarUpload} syncModalIsOpen={avatarUploadIsOpen} />
          </div>
        </div>
        <div>
          {hasAvatar ? (
            <Alert alertType={'information'} body={photoInfoMessage} accent={false} />
          ) : (
            <Alert alertType={'error'} body={noPhotoAlert} accent={false} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AvatarSection;
